import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../shared/types/common";
import { useNavigate } from "react-router-dom";
import { useForm } from "antd/lib/form/Form";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import {
  deleteAccount,
  getAccountsList,
} from "../../core/store/reducers/accounts.slice";
import { Space, Button, Col, Row, Modal, Drawer } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import { BiFilterAlt } from "react-icons/bi";
import ContentHeaderComponent from "../../shared/components/content-header/content-header.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import CreateEditAccountsComponent from "./components/create-edit-accounts.component";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { allPermissions } from "../../shared/constants/constants";

const ManageAccountsPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const { loadingAccount, accountList, pagination } = useSelector(
    (state: RootState) => state.account
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [accountIdToDelete, setAccountIdToDelete] = useState("");
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [accountListData, setAccountListData] = useState([] as any);
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({} as any);
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "" });
  const [openLogs, setOpenLogs] = useState(false);
  useEffect(() => {
    loadList();
  }, [filters, sorter]);

  const loadList = (page: any = null) => {
    const data = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pagination?.limit || TABLE_ROWS_COUNT,
      ...sorter,
    };
    let filter = {} as any;
    Object.keys(filters)?.map((val: any) => {
      if (filters[val]) filter[val] = filters[val];
    });
    dispatch(getAccountsList({ params: data, body: filter }));
  };

  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };
  const editEntityListner = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };

  useEffect(() => {
    setAccountListData(
      accountList?.map((obj: any, index: number) => {
        return { key: index, ...obj };
      })
    );
  }, [accountList]);
  const onFilterApply = (values: any) => {
    setFilters(values); //filters
  };
  const onPageChange = (pageNumber: number) => {
    // loadList((pageNumber - 1) * pagination?.limit);
    loadList(pageNumber);
  };
  const hasAnyAccountAccess =
  accessAllowed(myPermissions, allPermissions.UPDATE_ACCOUNT) ||
  accessAllowed(myPermissions, allPermissions.DELETE_ACCOUNT) ||
  accessAllowed(myPermissions, allPermissions.ACCOUNT_ACTIVITY_LOGS);
  const columns = [
    {
      title: "Client Name",
      dataIndex: "accountName",
      sorter: false,
      key: "accountName",
    },
    {
      title: "Client Code",
      dataIndex: "accountCode",
      sorter: false,
      key: "accountCode",
    },
    {
      title: "Client Legal Entity",
      dataIndex: "clientName",
      sorter: false,
      key: "clientName",
    },
    {
      title: "Entity",
      dataIndex: "entityName",
      sorter: false,
      key: "entity",
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: false,
      key: "status",
    },
    ...(hasAnyAccountAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            render: (text: any, record: any) => (
              <Space>
                {accessAllowed(myPermissions, allPermissions.UPDATE_ACCOUNT) && (
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                  />
                )}
                {accessAllowed(myPermissions, allPermissions.DELETE_ACCOUNT) && (
                  <Button
                    type="link"
                    onClick={() => handleDeleteModal(record?.accountId)}
                    icon={<DeleteOutlined style={{color:"#FF0000"}} />}
                  />
                )}
                {accessAllowed(myPermissions, allPermissions.ACCOUNT_ACTIVITY_LOGS) && (
                  <Button
                    type="link"
                    onClick={() => handleOpenLogs(record)}
                    icon={<EyeOutlined />}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  const handleDelete = (accountId: string) => {
    dispatch(deleteAccount(accountId)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("Client Deleted Successfully", "success");
        loadList();
        setDeleteModalVisible(false); // Close the modal after successful deletion
      }
    });
  };

  const handleOpenLogs = (record: any) => {
    setOpenLogs(true);
    setAccountIdToDelete(record?.accountId);
    setSelectedEntity(record);
  };

  const onCloseLogs = () => {
    setOpenLogs(false);
    setAccountIdToDelete("");
  };

  const handleDeleteModal = (accountId: string) => {
    setDeleteModalVisible(true);
    setAccountIdToDelete(accountId);
  };

  const onDeleteConfirm = () => {
    handleDelete(accountIdToDelete);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setAccountIdToDelete("");
  };

  const onChangeHandler = (_pagination: any, _filters: any, _sorter: any) => {
    if (_sorter.order === undefined) {
      setSorter({ sort_by: "", sort_type: "" });
    } else {
      let order = "";
      let columnKey = "";
      if (_sorter.order === "ascend") order = "asc";
      else order = "desc";

      if (_sorter.columnKey === "enabled") {
        columnKey = "status";
      } else if (_sorter.columnKey === "displayName") {
        columnKey = "name";
      } else {
        columnKey = _sorter.columnKey;
      }
      setSorter({ sort_by: columnKey, sort_type: order });
    }
  };

  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };

  useEffect(() => {
    loadList();
  }, []);
  const onValuesChange = (value: any, _allValues: any) => {};
  const formProps = {
    form: form,
    name: "manage-client-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "accountName",
        label: "",
        placeholder: "Client Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
    ],
    initialValues: {
      name: "",
    },
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: onValuesChange,
  };

  const headerButtons = (
    <Space>
      {accessAllowed(myPermissions, allPermissions.CREATE_ACCOUNT) && (
        <Button
          type="primary"
          // size="large"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New {!screenType?.isPortrait && "Client"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={
          <span role="img" className="anticon">
            <BiFilterAlt />
          </span>
        }
        onClick={() => {
          setFilterVisible(!filterVisible);
        }}
      ></Button>
    </Space>
  );

  return (
    <>
      <div
        className="mx-6 mt-2 bg-primary-white-blueish border-primary-gray-02"
        style={{ borderBottom: "none!important" }}
      >
        <Row>
          <Col span={24}>
            <ContentHeaderComponent
              title="Manage Client"
              subtitle=""
              buttons={headerButtons}
            />
          </Col>
        </Row>
        {filterVisible && (
          <Row className="bg-filter">
            <Col span={24} className="py-1">
              <div className="px-3 table-filter-form">
                <GenericFormComponent {...formProps} />
              </div>
            </Col>
          </Row>
        )}
      </div>
      <DataTableComponent
        pagination={true}
        loading={loadingAccount}
        dataSource={accountListData}
        columns={columns}
        onChange={onChangeHandler}
        defaultPage={0}
        currentPage={pagination.page}
        totalRows={pagination?.total}
        onPageChange={onPageChange}
        // maxWidth="2200px"
      />
      <Modal
        title="Confirm Delete"
        visible={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
      >
        <p>Are you sure you want to delete this client ?</p>
      </Modal>
      {createEditOpen && (
        <CreateEditAccountsComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}
      {openLogs && (
        <Drawer open={openLogs} onClose={onCloseLogs} title="Log Details">
          <ActivityLogComponent
            id={accountIdToDelete}
            record={selectedEntity}
            module="Client"
          />
        </Drawer>
      )}
    </>
  );
};
export default ManageAccountsPage;
